import React, { useState } from 'react';
import { 
  Box, 
  ListItem, 
  Typography, 
  Paper, 
  Grid, 
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Avatar,
  Collapse,
  IconButton,
  Button,
  ButtonGroup
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ContentCopy,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import LookupHitDialog from '../components/LookupHitDialog';
import SearchHitsDialog from '../components/SearchHitsDialog';

// Helper function to format large numbers
const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined) return '-';
  if (typeof num === 'number') {
    return num.toFixed(decimals);
  }
  return '-';
};

// Helper function to format currency
const formatCurrency = (num) => {
  if (num === null || num === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(num);
};

// Helper function to format percentage
const formatPercent = (num, decimals = 2) => {
  if (num === null || num === undefined) return '-';
  if (typeof num === 'number') {
    return `${num.toFixed(decimals)}%`;
  }
  return '-';
};

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return dateString;
  }
};

// Component to render wallet profile header
const WalletProfileSection = ({ wallet, hit }) => {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {wallet.pfp && (
          <Avatar
            alt={wallet.name}
            src={wallet.pfp}
            sx={{
              width: 80,
              height: 80,
              backgroundColor: 'grey.300',
              border: '2px solid',
              borderColor: 'primary.main',
              flexShrink: 0
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {wallet.name || 'Unknown Wallet'}
            </Typography>
            {wallet.pseudonym && (
              <Chip 
                label={wallet.pseudonym}
                size="small"
                variant="outlined"
                color="primary"
              />
            )}
          </Box>
          {wallet.bio && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
              "{wallet.bio}"
            </Typography>
          )}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              p: 1, 
              backgroundColor: 'grey.100',
              borderRadius: 1,
              cursor: 'pointer',
              wordBreak: 'break-all'
            }}
            onClick={() => copyToClipboard(wallet.user_id)}
          >
            <Typography variant="caption" sx={{ fontFamily: 'monospace', flex: 1 }}>
              {wallet.user_id}
            </Typography>
            <ContentCopy sx={{ fontSize: '0.9rem', flexShrink: 0 }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Component to render semantic embeddings
const SemanticEmbeddingsSection = ({ wallet, onEmbeddingClick }) => {
  const embeddings = [];
  
  if (wallet.sem_embed || wallet.text_vector) {
    embeddings.push({ key: 'sem_embed', label: 'Semantic Embedding' });
  }
  
  for (let i = 1; i <= 5; i++) {
    const embedKey = `embed${i}`;
    if (wallet[embedKey]) {
      embeddings.push({ key: embedKey, label: `Cluster ${i}` });
    }
  }

  if (embeddings.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <ButtonGroup size="small" variant="outlined">
        {embeddings.map((embed) => (
          <Button 
            key={embed.key}
            onClick={() => onEmbeddingClick(embed.key, embed.label)}
          >
            {embed.label}
          </Button>
        ))}
      </ButtonGroup>
    </Box>
  );
};

// Component to render AI labels (combined with color differentiation)
const AILabelsSection = ({ primaryLabels, secondaryLabels }) => {
  if ((!primaryLabels || primaryLabels.length === 0) && (!secondaryLabels || secondaryLabels.length === 0)) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        AI Labels
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {/* Primary Labels */}
        {primaryLabels && primaryLabels.map((label, idx) => (
          <Chip
            key={`primary-${idx}`}
            label={label}
            color="secondary"
            size="small"
            variant="outlined"
          />
        ))}

        {/* Secondary Labels */}
        {secondaryLabels && secondaryLabels.map((label, idx) => (
          <Chip
            key={`secondary-${idx}`}
            label={label}
            color="warning"
            size="small"
            variant="outlined"
          />
        ))}
      </Box>
    </Box>
  );
};

// Component to render tags (primary and secondary)
const TagsSection = ({ primaryTags, secondaryTags }) => {
  if ((!primaryTags || primaryTags.length === 0) && (!secondaryTags || secondaryTags.length === 0)) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Tags
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {/* Primary Tags */}
        {primaryTags && primaryTags.map((tag, idx) => (
          <Chip
            key={`primary-tag-${idx}`}
            label={tag}
            color="success"
            size="small"
            variant="outlined"
          />
        ))}

        {/* Secondary Tags */}
        {secondaryTags && secondaryTags.map((tag, idx) => (
          <Chip
            key={`secondary-tag-${idx}`}
            label={tag}
            color="info"
            size="small"
            variant="outlined"
          />
        ))}
      </Box>
    </Box>
  );
};

// Component to render wallet performance metrics
const PerformanceMetricsSection = ({ wallet }) => {
  const pnlPercent = wallet.volume && wallet.volume !== 0 
    ? (wallet.pnl / wallet.volume) * 100 
    : 0;
  
  const isProfitable = wallet.pnl >= 0;
  const ProfitIcon = isProfitable ? TrendingUp : TrendingDown;
  const profitColor = isProfitable ? '#2e7d32' : '#d32f2f';

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Metrics
      </Typography>
      <Grid container spacing={1}>
        {/* Volume */}
        <Grid>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              backgroundColor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200'
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
              Volume
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
              {formatCurrency(wallet.volume)}
            </Typography>
          </Paper>
        </Grid>

        {/* PnL */}
        <Grid>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              backgroundColor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <ProfitIcon sx={{ fontSize: '1rem', color: profitColor }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                PnL
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: profitColor }}>
              {formatCurrency(wallet.pnl)}
            </Typography>
          </Paper>
        </Grid>

        {/* PnL % */}
        <Grid>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              backgroundColor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <ProfitIcon sx={{ fontSize: '1rem', color: profitColor }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Return
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: profitColor }}>
              {formatPercent(pnlPercent, 2)}
            </Typography>
          </Paper>
        </Grid>

        {/* Events Per Day */}
        {wallet.events_per_day !== null && wallet.events_per_day !== undefined && (
          <Grid>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                Events/Day
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                {formatNumber(wallet.events_per_day, 2)}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

// Component to render timestamps
const TimestampsSection = ({ wallet }) => {
  const hasAnyTimestamp = wallet.updated_at || wallet.full_update_timestamp || wallet.soft_update_timestamp;
  const hasEventsSinceUpdate = wallet.events_since_full_update !== null && wallet.events_since_full_update !== undefined;
  
  if (!hasAnyTimestamp && !hasEventsSinceUpdate) {
    return null;
  }

  const MetricBox = ({ label, value }) => (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        backgroundColor: 'grey.50',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200'
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Timestamps
      </Typography>
      <Grid container spacing={1}>
        {/* Updated At */}
        {wallet.updated_at && (
          <Grid>
            <MetricBox label="Updated" value={formatDate(wallet.updated_at)} />
          </Grid>
        )}

        {/* Full Update Timestamp */}
        {wallet.full_update_timestamp && (
          <Grid>
            <MetricBox label="Full Update" value={formatDate(wallet.full_update_timestamp)} />
          </Grid>
        )}

        {/* Soft Update Timestamp */}
        {wallet.soft_update_timestamp && (
          <Grid>
            <MetricBox label="Soft Update" value={formatDate(wallet.soft_update_timestamp)} />
          </Grid>
        )}

        {/* Events Since Full Update */}
        {hasEventsSinceUpdate && wallet.events_since_full_update > 0 && (
          <Grid>
            <MetricBox label="Events Since Update" value={formatNumber(wallet.events_since_full_update, 0)} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

// Component to render similar wallets
const SimilarWalletsSection = ({ primaryWallets, secondaryWallets }) => {
  const [expanded, setExpanded] = useState(false);

  if ((!primaryWallets || primaryWallets.length === 0) && (!secondaryWallets || secondaryWallets.length === 0)) {
    return null;
  }

  const renderWalletList = (wallets) => (
    <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
      <Table size="small">
        <TableBody>
          {wallets.map((wallet, idx) => (
            <TableRow key={idx}>
              <TableCell 
                sx={{ 
                  fontSize: '0.85rem', 
                  p: 0.75, 
                  fontFamily: 'monospace',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'grey.100' }
                }}
                onClick={() => navigator.clipboard.writeText(wallet)}
              >
                {wallet}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const totalWallets = (primaryWallets?.length || 0) + (secondaryWallets?.length || 0);

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          mb: expanded ? 1 : 0
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton
          size="small"
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s'
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Similar Wallets ({totalWallets})
        </Typography>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ pl: 4, pr: 1 }}>
          <Grid container spacing={2}>
            {/* Primary Similar Wallets */}
            {primaryWallets && primaryWallets.length > 0 && (
              <Grid>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Primary
                  </Typography>
                  {renderWalletList(primaryWallets)}
                </Box>
              </Grid>
            )}

            {/* Secondary Similar Wallets */}
            {secondaryWallets && secondaryWallets.length > 0 && (
              <Grid>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Secondary
                  </Typography>
                  {renderWalletList(secondaryWallets)}
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
};

// Component to render market interactions
const MarketInteractionsSection = ({ reactionsLog }) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedMarketId, setSelectedMarketId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!reactionsLog || reactionsLog.length === 0) {
    return null;
  }

  const handleOpenDialog = (marketId) => {
    setSelectedMarketId(marketId);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          mb: expanded ? 1 : 0
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton
          size="small"
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s'
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Market Interactions ({reactionsLog.length})
        </Typography>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ pl: 4, pr: 1 }}>
          {reactionsLog.map((reaction, idx) => (
            <Paper
              key={idx}
              elevation={0}
              sx={{
                mb: 1.5,
                p: 1.5,
                backgroundColor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 1
              }}
            >
              {/* Row 1: Market ID Button, Event, Side, Outcome, and Transaction Info */}
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleOpenDialog(reaction.item_id)}
                    sx={{ 
                      textTransform: 'none',
                      fontSize: '0.85rem',
                      fontFamily: 'monospace',
                      flexShrink: 0
                    }}
                  >
                    Market #{reaction.item_id}
                  </Button>

                  {reaction.event && (
                    <Chip 
                      label={reaction.event} 
                      size="small" 
                      variant="outlined"
                    />
                  )}

                  {reaction.side && (
                    <Chip 
                      label={reaction.side} 
                      size="small" 
                      variant="outlined"
                    />
                  )}

                  {reaction.outcome && (
                    <Chip 
                      label={reaction.outcome} 
                      size="small" 
                      variant="outlined"
                    />
                  )}

                  {reaction.shares !== null && reaction.shares !== undefined && 
                   reaction.price !== null && reaction.price !== undefined && (
                    <Chip 
                      label={`${formatNumber(reaction.shares, 2)} shares at $${formatNumber(reaction.price, 4)}`}
                      size="small" 
                      variant="outlined"
                    />
                  )}

                  {reaction.usdc !== null && reaction.usdc !== undefined && (
                    <Chip 
                      label={formatCurrency(reaction.usdc)}
                      size="small" 
                      variant="outlined"
                    />
                  )}
                </Box>

                {reaction.time && (
                  <Chip 
                    label={formatDate(reaction.time)}
                    size="small" 
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                  />
                )}
              </Box>

              {/* Row 2: Labels */}
              {reaction.labels && reaction.labels.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Labels
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                    {reaction.labels.join(', ')}
                  </Typography>
                </Box>
              )}

              {/* Row 3: Tags */}
              {reaction.tags && reaction.tags.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Tags
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                    {reaction.tags.join(', ')}
                  </Typography>
                </Box>
              )}
            </Paper>
          ))}
        </Box>
      </Collapse>

      <LookupHitDialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        index="polymarket-items"
        docId={selectedMarketId}
        title={`Market #${selectedMarketId}`}
      />
    </Box>
  );
};

// Component to render wallet ID
const DocumentInfoSection = ({ hit, wallet }) => {
  const walletId = wallet.user_id || hit._id;
  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
      <Typography 
        variant="caption" 
        sx={{ 
          color: 'text.secondary',
          wordBreak: 'break-all',
          fontSize: '0.7rem',
          fontFamily: 'monospace'
        }}
      >
        #{walletId}
      </Typography>
    </Box>
  );
};

const PolymarketWalletCard = ({ hit }) => {
  const { _source: wallet } = hit;
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [selectedEmbeddingLabel, setSelectedEmbeddingLabel] = useState(null);
  const [selectedEmbeddingKey, setSelectedEmbeddingKey] = useState(null);

  const handleEmbeddingClick = (embedKey, label) => {
    setSelectedEmbeddingKey(embedKey);
    setSelectedEmbeddingLabel(label);
    setSearchDialogOpen(true);
  };

  const handleSearchDialogClose = () => {
    setSearchDialogOpen(false);
    setSelectedEmbeddingLabel(null);
    setSelectedEmbeddingKey(null);
  };

  // Build dynamic payload with the actual embedding vector
  const buildSearchPayload = () => {
    return {
      index: "polymarket-items",
      origin: "frontend",
      feed_type: "semantic",
      include_vector: false,
      size: 10,
      vector: wallet[selectedEmbeddingKey]
    };
  };

  // Only build payload when dialog is open and embedding is selected
  const searchPayload = searchDialogOpen ? buildSearchPayload() : null;

  return (
    <ListItem 
      sx={{ 
        borderBottom: '1px solid #e0e0e0',
        flexDirection: 'column',
        alignItems: 'stretch',
        py: 2,
        px: 0
      }}
    >
      <Card
        elevation={1}
        sx={{
          width: '100%',
          backgroundColor: '#fafafa',
          overflow: 'visible'
        }}
      >
        <CardContent sx={{ pb: 2 }}>
          {/* Wallet Profile */}
          <WalletProfileSection wallet={wallet} hit={hit} />

          {/* Semantic Embeddings */}
          <SemanticEmbeddingsSection wallet={wallet} onEmbeddingClick={handleEmbeddingClick} />

          {/* AI Labels (Primary and Secondary) */}
          <AILabelsSection 
            primaryLabels={wallet.primary_labels} 
            secondaryLabels={wallet.secondary_labels}
          />

          {/* Tags (Primary and Secondary) */}
          <TagsSection 
            primaryTags={wallet.primary_tags} 
            secondaryTags={wallet.secondary_tags}
          />

          {/* Performance Metrics */}
          <PerformanceMetricsSection wallet={wallet} />

          {/* Timestamps */}
          <TimestampsSection wallet={wallet} />

          {/* Similar Wallets */}
          <SimilarWalletsSection 
            primaryWallets={wallet.primary_similar_wallets}
            secondaryWallets={wallet.secondary_similar_wallets}
          />

          {/* Market Interactions */}
          <MarketInteractionsSection reactionsLog={wallet.reactions_log} />

          {/* Wallet Details */}
          <DocumentInfoSection hit={hit} wallet={wallet} />
        </CardContent>
      </Card>

      {/* Search Results Dialog */}
      <SearchHitsDialog
        open={searchDialogOpen}
        title={`Test ${selectedEmbeddingLabel}`}
        endpoint="/search/semantic"
        payload={searchPayload}
        onClose={handleSearchDialogClose}
      />
    </ListItem>
  );
};

export default PolymarketWalletCard;
